import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Panel } from '../../components/charts';
import { money } from './Dashboard';

type Contract = {
  id: string;
  contractNo: string;
  title: string;
  currency: string;
  contractSum: string | number;
  retentionPct: string | number;
  status: string;
  contractor: { name: string };
  _count: { boqItems: number; variations: number; certificates: number };
};

type Contractor = { id: string; code: string; name: string };

export function Contracts({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [contracts, setContracts] = useState<Contract[] | null>(null);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    contractorId: '',
    contractNo: '',
    title: '',
    contractSum: '',
    retentionPct: '5',
    advanceAmount: '',
    advanceRecoveryPct: '',
  });

  const load = useCallback(async () => {
    try {
      const [c, k] = await Promise.all([
        api.get<{ contracts: Contract[] }>(`/cost/contracts?projectId=${projectId}`),
        api.get<{ contractors: Contractor[] }>('/cost/contractors'),
      ]);
      setContracts(c.contracts);
      setContractors(k.contractors);
      setForm((f) => ({ ...f, contractorId: f.contractorId || (k.contractors[0]?.id ?? '') }));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load contracts.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addContractor() {
    const name = window.prompt('Nama kontraktor:');
    if (!name?.trim()) return;
    const code = window.prompt('Kode singkat — dipakai juga oleh hse. nanti:');
    if (!code?.trim()) return;
    try {
      await api.post('/cost/contractors', { code: code.trim(), name: name.trim() });
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!form.contractorId || !form.contractNo.trim() || !form.title.trim()) return;
    setBusy(true);
    try {
      await api.post('/cost/contracts', {
        projectId,
        contractorId: form.contractorId,
        contractNo: form.contractNo.trim(),
        title: form.title.trim(),
        contractSum: Number(form.contractSum || 0),
        retentionPct: Number(form.retentionPct || 0),
        advanceAmount: Number(form.advanceAmount || 0),
        advanceRecoveryPct: Number(form.advanceRecoveryPct || 0),
      });
      setForm((f) => ({ ...f, contractNo: '', title: '', contractSum: '', advanceAmount: '' }));
      setNotice('Kontrak dibuat. Langkah berikutnya: masukkan BOQ dan tautkan tiap item ke aktivitas.');
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal membuat kontrak.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!contracts) return <Loading rows={4} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Kontrak</h1>
          <p>Basisnya BOQ terukur — item, kuantitas, harga satuan, dan aktivitas yang menyertainya.</p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <Panel title="Kontrak baru" note="Kontraktor dipakai bersama dengan hse. lewat model yang sama.">
        <form onSubmit={create}>
          <div className="filters">
            <select
              className="select"
              value={form.contractorId}
              onChange={(e) => setForm({ ...form, contractorId: e.target.value })}
              required
            >
              <option value="">Kontraktor…</option>
              {contractors.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.code} — {k.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addContractor}>
              + Kontraktor
            </button>
            <input
              className="input"
              placeholder="Nomor kontrak"
              value={form.contractNo}
              onChange={(e) => setForm({ ...form, contractNo: e.target.value })}
              required
            />
            <input
              className="input"
              placeholder="Judul"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <input
              className="input"
              type="number"
              placeholder="Nilai kontrak"
              value={form.contractSum}
              onChange={(e) => setForm({ ...form, contractSum: e.target.value })}
            />
            <input
              className="input"
              type="number"
              step="0.1"
              placeholder="Retensi %"
              title="Persentase retensi"
              value={form.retentionPct}
              onChange={(e) => setForm({ ...form, retentionPct: e.target.value })}
            />
            <button className="btn btn-primary" disabled={busy}>
              Buat
            </button>
          </div>
        </form>
      </Panel>

      {contracts.length === 0 ? (
        <Empty title="Belum ada kontrak" body="Buat satu di atas untuk memulai." />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Nomor</th>
                <th>Judul</th>
                <th>Kontraktor</th>
                <th>Nilai</th>
                <th>Retensi</th>
                <th>BOQ</th>
                <th>IPC</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} onClick={() => nav(`/contracts/${c.id}`)}>
                  <td className="num">{c.contractNo}</td>
                  <td>{c.title}</td>
                  <td>{c.contractor.name}</td>
                  <td>{money(Number(c.contractSum), c.currency)}</td>
                  <td>{Number(c.retentionPct)}%</td>
                  <td>{c._count.boqItems}</td>
                  <td>{c._count.certificates}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
